import React, { useCallback, useContext, useMemo, useState } from "react";
import { UI, ScreenSize } from "@wwimmo/ui";
import { observer } from "mobx-react-lite";
import { RootStoreContext } from "src/stores/RootStore";
import { useTranslation } from "react-i18next";
import styles from "./CustomerSettingsList.module.css";
import { useSaveFormByShortcut } from "src/hooks/form/useSaveFormByShortcut";
import { ICustomerSettingRow } from "src/stores/CustomerSettingsStore";

interface ICustomerSettingsListProps {
    configurations: ICustomerSettingRow[];
    listIdentifier: string;
    canEdit: boolean;
}

const MAX_VALUE_PREVIEW_LENGTH = 120;
const LARGE_VALUE_INPUT_ROWS = 4;
const HUGE_VALUE_INPUT_ROWS = 8;

function getTextareaRows(value: string) {
    const textLines = value.split("\n");
    const visualRows = textLines.reduce((rows, line) => rows + Math.max(1, Math.ceil(line.length / 80)), 0);
    const minimumRows = value.length > MAX_VALUE_PREVIEW_LENGTH ? 3 : 1;

    return Math.max(minimumRows, Math.min(12, visualRows));
}

function getValueInputSizeClass(value: string, classNames: typeof styles) {
    const rows = getTextareaRows(value);

    if (rows >= HUGE_VALUE_INPUT_ROWS) {
        return classNames.hugeValueInput;
    }

    if (rows >= LARGE_VALUE_INPUT_ROWS) {
        return classNames.largeValueInput;
    }

    return "";
}

function IconButtonLabel(props: { icon: React.ComponentProps<typeof UI.Icon>["icon"]; text: string }) {
    return (
        <>
            <UI.Icon icon={props.icon} />
            <span className={styles.visuallyHidden}>{props.text}</span>
        </>
    );
}

function EditModeButtonLabel(props: { editMode: boolean; label: string }) {
    return (
        <span className={styles.toolbarButtonLabel}>
            {props.label}
            <span className={styles.toolbarButtonIcon}>
                <UI.Icon icon={props.editMode ? UI.SVGIcon.Close : UI.SVGIcon.Edit} size={20} />
            </span>
        </span>
    );
}

async function copyToClipboard(value: string) {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
    }
    const elem = document.createElement("textarea");
    elem.value = value;
    document.body.appendChild(elem);
    elem.select();
    document.execCommand("copy");
    document.body.removeChild(elem);
}

interface ICustomerSettingValueEditorProps {
    canEdit: boolean;
    row: ICustomerSettingRow;
}

const CustomerSettingsSaveShortcut = observer(() => {
    const { customerSettingsStore } = useContext(RootStoreContext);

    useSaveFormByShortcut({
        saveFormFunction: customerSettingsStore.saveDirtyValues,
        isFormDirty: customerSettingsStore.editMode && customerSettingsStore.hasDirtyValues
    });

    return null;
});

const CustomerSettingValueEditor = observer((props: ICustomerSettingValueEditorProps) => {
    const { customerSettingsStore } = useContext(RootStoreContext);
    const value = customerSettingsStore.getEditedValue(props.row.key);
    const validationError = customerSettingsStore.validationErrors[props.row.key];
    const valueInputClassName = `${styles.valueInput} ${getValueInputSizeClass(value, styles)}`;

    const handleValueChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        customerSettingsStore.setEditedValue(event.target.id, event.target.value);
    }, [customerSettingsStore]);

    const handleSaveValueClick = useCallback(() => {
        if (!customerSettingsStore.isDirty(props.row.key) || customerSettingsStore.saving) {
            return;
        }

        customerSettingsStore.saveValue(props.row.key);
    }, [customerSettingsStore, props.row.key]);

    return (
        <div className={styles.editValueCell}>
            <UI.Input
                as="textarea"
                className={valueInputClassName}
                id={props.row.key}
                value={value}
                readOnly={!props.canEdit}
                onChange={handleValueChange}
                isInvalid={Boolean(validationError)}
                errorMsg={validationError}
            />
            <UI.Icon
                className={styles.saveIcon}
                icon={UI.SVGIcon.Save}
                onClick={handleSaveValueClick}
            />
        </div>
    );
});

const CustomerSettingsListBase = (props: ICustomerSettingsListProps) => {
    const { uiStore, navStore, customerSettingsStore } = useContext(RootStoreContext);
    const { t } = useTranslation();
    const [expandedKey, setExpandedKey] = useState<string>("");
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    if (!navStore.listMap.has(props.listIdentifier)) {
        navStore.setListMapValue(props.listIdentifier, {});
    }

    const listParameters = navStore.getListMapValue(props.listIdentifier);
    const currentSearchQuery = listParameters?.searchQuery ?? "";
    const [searchQuery, setSearchQuery] = useState(currentSearchQuery);
    const filteredRows = useMemo(() => {
        const normalizedQuery = searchQuery.toLowerCase().trim();

        if (normalizedQuery.length === 0) {
            return props.configurations;
        }

        return props.configurations.filter((row) => {
            const keyText = row.key.toLowerCase();
            const valueText = (row.value ?? "").toLowerCase();
            const defaultText = (row.default ?? "").toLowerCase();

            return (
                keyText.includes(normalizedQuery) ||
                valueText.includes(normalizedQuery) ||
                defaultText.includes(normalizedQuery)
            );
        });
    }, [props.configurations, searchQuery]);
    const copyValues = useMemo(() => {
        const values = new Map<string, string>();
        filteredRows.forEach((row) => {
            if (row.value) {
                values.set(`${row.key}-value`, row.value);
            }
            if (row.default) {
                values.set(`${row.key}-default`, row.default);
            }
        });

        return values;
    }, [filteredRows]);

    const handleToggleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        const key = event.currentTarget.id;
        setExpandedKey((previousKey) => (previousKey === key ? "" : key));
    }, []);

    const handleCopyClick = useCallback(async (event: React.MouseEvent<HTMLElement>) => {
        const copyId = event.currentTarget.id;
        const value = copyValues.get(copyId);
        if (!value) {
            return;
        }

        await copyToClipboard(value);
        setCopiedKey(copyId);
        setTimeout(() => setCopiedKey((prev) => (prev === copyId ? null : prev)), 2000);
    }, [copyValues]);

    const handleEditModeClick = useCallback(() => {
        customerSettingsStore.toggleEditMode();
    }, [customerSettingsStore]);

    const handleEditModeKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            customerSettingsStore.toggleEditMode();
        }
    }, [customerSettingsStore]);

    const renderValueCell = useCallback((
        rawValue: string | null,
        rowKey: string,
        copyId: string,
        showToggle: boolean,
        wrapperClassName?: string
    ) => {
        const checkboxName = `${copyId}-checkbox`;

        if (rawValue === "true") {
            return <UI.Checkbox name={checkboxName} checked={true} disabled={true} />;
        }
        if (rawValue === "false") {
            return <UI.Checkbox name={checkboxName} checked={false} disabled={true} />;
        }
        if (!rawValue) {
            return "—";
        }

        const isExpandable = rawValue.length > MAX_VALUE_PREVIEW_LENGTH;
        const isExpanded = expandedKey === rowKey;
        const valueTextClass = isExpanded ? styles.valueTextExpanded : styles.valueTextCollapsed;

        if (!isExpandable) {
            return <span className={styles.valueText}>{rawValue}</span>;
        }

        return (
            <div className={`${styles.valueCell} ${wrapperClassName ?? ""}`}>
                <button
                    type="button"
                    className={`${styles.valueText} ${styles.valueTextButton} ${valueTextClass}`}
                    id={rowKey}
                    onClick={handleToggleClick}
                    aria-label={
                        isExpanded
                            ? t("customer_settings.collapse_value")
                            : t("customer_settings.expand_value")
                    }
                >
                    {rawValue}
                </button>
                <div className={styles.actionColumn}>
                    <div className={styles.actionRow}>
                        {showToggle && isExpandable ? (
                            <UI.Button
                                className={styles.toggleButton}
                                id={rowKey}
                                variant="link"
                                label={
                                    <IconButtonLabel
                                        icon={isExpanded ? UI.SVGIcon.ChevronDownThin : UI.SVGIcon.ChevronRightThin}
                                        text={
                                            isExpanded
                                                ? t("customer_settings.collapse_value")
                                                : t("customer_settings.expand_value")
                                        }
                                    />
                                }
                                onClick={handleToggleClick}
                            />
                        ) : null}
                        {isExpanded ? (
                            <div className={styles.actionColumn}>
                                {copiedKey === copyId ? (
                                    <span className={styles.copiedText}>{t("labels.copied")}</span>
                                ) : null}
                                <UI.Button
                                    className={styles.copyButton}
                                    id={copyId}
                                    variant="link"
                                    label={
                                        <IconButtonLabel
                                            icon={UI.SVGIcon.Documents}
                                            text={t("customer_settings.copy_value")}
                                        />
                                    }
                                    onClick={handleCopyClick}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    }, [copiedKey, expandedKey, handleCopyClick, handleToggleClick, t]);

    const renderEditableValueCell = useCallback((row: ICustomerSettingRow, rowKey: string) => {
        if (!customerSettingsStore.editMode) {
            return (
                <div
                    className={styles.editableValue}
                >
                    {renderValueCell(row.value, rowKey, `${rowKey}-value`, false, styles.valueColumn)}
                </div>
            );
        }

        return <CustomerSettingValueEditor canEdit={props.canEdit} row={row} />;
    }, [
        customerSettingsStore,
        props.canEdit,
        renderValueCell
    ]);

    const renderItem = useCallback((row: ICustomerSettingRow, screenSize: ScreenSize) => {
        const listRows = [
            <UI.List.Row key={"r-1"}>
                <UI.List.Cell key={"c-1"} colspan={1} value={<span className={styles.keyText}>{row.key}</span>} />
                <UI.List.Cell
                    key={"c-2"}
                    colspan={1}
                    value={renderEditableValueCell(row, row.key)}
                />
                <UI.List.Cell
                    key={"c-3"}
                    colspan={1}
                    value={renderValueCell(row.default, row.key, `${row.key}-default`, true, styles.defaultCell)}
                />
            </UI.List.Row>
        ];

        return <UI.List.Item key={row.key} screenSize={screenSize} rows={listRows} />;
    }, [renderEditableValueCell, renderValueCell]);

    const renderHeader = useCallback(() => {
        const headerRows = [
            <UI.List.Row key={"row-1"}>
                <UI.List.Cell key={"c-1"} colspan={1} value={t("customer_settings.key")} />
                <UI.List.Cell key={"c-2"} colspan={1} value={t("customer_settings.value")} />
                <UI.List.Cell key={"c-3"} colspan={1} value={t("labels.default")} />
            </UI.List.Row>
        ];

        return <UI.List.Header rows={headerRows} sticky={true} top={130} />;
    }, [t]);

    const handleSearchInput = useCallback((searchArguments: { searchQuery?: string }) => {
        const nextQuery = searchArguments.searchQuery ?? "";
        setSearchQuery(nextQuery);
        navStore.setListMapValue(
            props.listIdentifier,
            listParameters ? { ...listParameters, searchQuery: nextQuery } : { searchQuery: nextQuery }
        );
    }, [listParameters, navStore, props.listIdentifier]);

    return (
        <>
            <CustomerSettingsSaveShortcut />
            {props.canEdit ? (
                <div className={styles.toolbar}>
                    <div
                        className={styles.toolbarButton}
                        onClick={handleEditModeClick}
                        onKeyDown={handleEditModeKeyDown}
                        role="button"
                        tabIndex={0}
                    >
                        <EditModeButtonLabel
                            editMode={customerSettingsStore.editMode}
                            label={
                                customerSettingsStore.editMode
                                    ? t("customer_settings.exit_edit_mode")
                                    : t("customer_settings.edit_mode")
                            }
                        />
                    </div>
                </div>
            ) : null}
            <UI.List.BasicList
                screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={filteredRows}
                renderItem={renderItem}
                renderHeader={renderHeader}
                onSearch={handleSearchInput}
                initialSearchQuery={currentSearchQuery}
            />
        </>
    );
};

export const CustomerSettingsList = observer(CustomerSettingsListBase);
